import { CurrencyCode, FeatureAccess, Language, MeasuredBy } from './common';

export type DynamicLayoutDashboardKey =
  | 'asset_dashboard'
  | 'portfolio_dashboard'
  | 'deals_dashboard'
  | 'tenants';

export type DynamicLayoutDashboardSectionKey =
  | 'commercial'
  | 'multifamily'
  | 'total'
  | 'volume'
  | 'tenants_list'
  | 'tenants_dashboard';

export type DynamicLayoutChart =
  | 'default_kpis'
  | 'assets_country_code'
  | 'units_type_of_use'
  | 'years_of_construction'
  | 'lease_start_date_years'
  | 'units_apartment_sizes'
  | 'unit_type_lease_ends'
  | 'units_top_tenants'
  | 'lease_end_date_years'
  | 'lease_end_date_tenants'
  | 'units_rental_prices'
  | 'assets_data'
  | 'assets_map'
  | 'deal_stage_id_total'
  | 'deals_status_date_total'
  | 'deals_canceled_reason_total'
  | 'deals_captain_id_total'
  | 'deals_broker_name_total'
  | 'deals_risk_profile_total'
  | 'deal_stage_id_volume'
  | 'deals_status_date_volume'
  | 'deals_canceled_reason_volume'
  | 'deals_captain_id_volume'
  | 'deals_broker_name_volume'
  | 'deals_risk_profile_volume'
  | 'unit_type_rent_plots'
  | 'unit_type_rent_bars'
  | 'risks_kpis'
  | 'latest_year_vs_trigger'
  | 'implied_rating_change'
  | 'kpis_lender'
  | 'kpis_asset_manager'
  | 'industry_primary_label'
  | 'rent_implied_rating'
  | 'tenant_implied_rating_label';

export interface DynamicLayout {
  asset_dashboard: {
    [key in DynamicLayoutDashboardSectionKey]?: DynamicLayoutChart[];
  };
  portfolio_dashboard: {
    [key in DynamicLayoutDashboardSectionKey]?: DynamicLayoutChart[];
  };
  deals_dashboard: {
    [key in DynamicLayoutDashboardSectionKey]?: DynamicLayoutChart[];
  };
  tenants: {
    [key in DynamicLayoutDashboardSectionKey]?: DynamicLayoutChart[];
  };
}

export interface LoanSensitivitySettingsRange {
  upper: number;
  lower: number;
}

export interface LoanSensitivitySettings {
  dscr: LoanSensitivitySettingsRange;
  debt_yield: LoanSensitivitySettingsRange;
  ltv: LoanSensitivitySettingsRange;
}

export type Stylesheet = {
  primary_color: string;
  primary_color_map: string;
  primary_color_sidebar: string;
  primary_text_color: string;
  primary_text_color_map: string;
  secondary_color: string;
  secondary_color_map: string;
  secondary_color_sidebar: string;
  secondary_text_color: string;
  secondary_text_color_map: string;
  third_color: string;
  third_color_sidebar: string;
  third_text_color: string;
  fourth_color: string;
};

export type ColorKey = Extract<keyof Stylesheet, string>;

interface CompanyAttr {
  name: string;
  currency_code: CurrencyCode;
  measured_by: MeasuredBy;
  region: string;
  description: string;
  website_url: string;
  image_url: string;
  created_at: number;
  updated_at: number;
  internal_users_limit: number;
  internal_users_count: number;
  external_users_limit: number;
  external_users_count: number;
  sub_domain: string;
  language: Language;
  avatar_url: string;
  mark_logo_url: string;
  timeout: number;
  invitation_template_id: string;
  layouts: DynamicLayout | null;
  style: Stylesheet | null;
  feature_access: FeatureAccess[];
  company_ref_uuid?: string;
  quiqproperty_secret_key?: string;
  quiqproperty_public_key?: string;
  loan_sensitivity_settings: LoanSensitivitySettings | null;
}
export interface Company {
  type: string;
  id: string;
  attributes: CompanyAttr;
  relationships?: {
    users: {
      type: string;
      id: string;
      attributes: {
        email: string;
        user_type: string;
        portfolios: [];
      };
    };
  };
}

export type CompanyAttrNames = Extract<keyof CompanyAttr, string>;

export const companyUpdateAttributes = [
  'name',
  'currency_code',
  'measured_by',
  'internal_users_limit',
  'external_users_limit',
  'sub_domain',
  'language',
  'layouts',
  'quiqproperty_secret_key',
  'quiqproperty_public_key',
  'loan_sensitivity_settings',
];

export const companyAttributes = [
  'name',
  'currency_code',
  'measured_by',
  'region',
  'description',
  'website_url',
  'image_url',
  'created_at',
  'updated_at',
  'style',
  'internal_users_limit',
  'external_users_limit',
  'sub_domain',
  'language',
  'avatar_url',
  'mark_logo_url',
  'timeout',
  'invitation_template_id',
  'layouts',
  'internal_users_count',
  'external_users_count',
  'feature_access',
  'quiqproperty_secret_key',
  'quiqproperty_public_key',
  'loan_sensitivity_settings',
];
